/**
 * Visits the room could not place, waiting on a human.
 *
 * These are darts that were genuinely thrown. A tablet queued them offline and
 * by the time it reconnected the world had moved — the board was taken over,
 * the match was forfeited, or its leg sequence could not be reconciled. V1
 * wrote them into whichever leg happened to be open, which is how real scores
 * ended up in the wrong leg undetectably; V2 refuses and parks them here.
 *
 * ASSIGN OR DISCARD, and discard is not a tidy-up. It throws away a score
 * somebody threw, so the row stays with its status changed rather than being
 * deleted and the system log records who decided.
 *
 * ASSIGN IS ONLY OFFERED ON A MATCH STILL IN PLAY. A finished match has a
 * winner, the bracket has moved past it and stats may be written; slotting a
 * dart back into that would unwind further than this screen can show. The
 * server refuses it too — this only avoids offering what would be rejected.
 *
 * Hidden entirely when there is nothing pending, which is the normal state. A
 * permanently-present empty panel is one an admin learns to stop seeing.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api.js';
import { formatStampDate } from '../../lib/format.js';

/**
 * Roughly when this happened, from either of the two stamps carried here.
 *
 * thrown_at is the tablet's own ISO clock and received_at is SQLite's
 * 'YYYY-MM-DD HH:MM:SS' in UTC with nothing marking it as such — so the second
 * shape is given its Z before being read, or the browser would take it as
 * local and shift it by an hour through the summer.
 *
 * Not formatDateTime: that one takes a date and a separate time column, which
 * is a different shape from either of these.
 */
function whenThrown(value) {
  if (!value) return null;

  const normalised = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`;
  const parsed = new Date(normalised);

  if (Number.isNaN(parsed.getTime())) return String(value);

  return `${formatStampDate(value)}, ` +
    parsed.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

const REASONS = {
  board_taken_over:    'Board was taken over while this tablet was offline',
  match_completed:     'Match had already finished',
  match_forfeit:       'Match had been forfeited',
  player_not_in_match: 'Player is not in that match',
  leg_sequence_gap:    'Leg sequence did not line up',
  leg_not_found:       'Leg could not be identified',
};

export default function HeldVisitsPanel({ venueId, tournamentId, refreshKey, onResolved }) {
  const [held, setHeld]   = useState([]);
  const [legs, setLegs]   = useState([]);
  const [busy, setBusy]   = useState(null);
  const [error, setError] = useState(null);
  const [legFor, setLegFor] = useState({});

  const load = useCallback(async () => {
    if (venueId == null || tournamentId == null) return;

    const { data } = await apiGet(
      `/api/venues/${venueId}/tournaments/${tournamentId}/held-visits`
    );
    setHeld(data?.heldVisits ?? []);
    setLegs(data?.legs ?? []);
  }, [venueId, tournamentId]);

  // Reloaded when the room says a visit was held, so a stray dart appears here
  // while the admin is looking at the page rather than on the next refresh.
  useEffect(() => { load(); }, [refreshKey, load]);

  const resolve = async (item, action) => {
    setBusy(item.id);
    setError(null);

    const body = action === 'assign'
      ? { action, legId: Number(legFor[item.id]) }
      : { action };

    const { error: err } = await apiPost(
      `/api/venues/${venueId}/tournaments/${tournamentId}/held-visits/${item.id}`,
      body
    );

    setBusy(null);

    if (err) { setError(err); return; }

    await load();
    onResolved?.();
  };

  if (held.length === 0) return null;

  return (
    <section className="hd-card hd-held">
      <div className="hd-row-between">
        <h2 className="hd-section-title" style={{ margin: 0 }}>
          Unplaced scores ({held.length})
        </h2>
        <span className="hd-badge hd-badge-warning">Needs a decision</span>
      </div>

      <p className="hd-meta">
        Thrown on a board that was offline, and the room could not work out where
        they belong. Put each one in a leg, or discard it.
      </p>

      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <ul className="hd-held-list">
        {held.map((item) => {
          // Only legs of the match it came from, and only while that match can
          // still take a visit.
          const matchLegs = legs.filter((l) => l.match_id === item.match_id);
          const placeable = item.match_status === 'pending' || item.match_status === 'live';
          const chosen    = legFor[item.id] ?? '';

          return (
            <li key={item.id} className="hd-held-item">
              <div className="hd-held-what">
                <strong>{item.player_name ?? `Entry ${item.player_id}`}</strong>
                <span className="hd-held-score">{item.is_bust ? 'Bust' : item.score}</span>
                <span className="hd-meta">
                  {item.darts_used} dart{item.darts_used === 1 ? '' : 's'}
                  {item.match_board_number != null && ` · board ${item.match_board_number}`}
                  {item.leg_number != null && ` · leg ${item.leg_number}`}
                </span>
              </div>

              <div className="hd-held-why">
                <span className="hd-meta">{REASONS[item.reason] ?? item.reason}</span>
                {/* The tablet's own clock — roughly when in the evening this
                    happened, not something to order other records against. */}
                <span className="hd-meta">
                  {whenThrown(item.thrown_at ?? item.received_at)}
                </span>
              </div>

              <div className="hd-held-actions">
                {placeable && matchLegs.length > 0 ? (
                  <select
                    className="hd-select"
                    value={chosen}
                    disabled={busy === item.id}
                    onChange={(e) => setLegFor({ ...legFor, [item.id]: e.target.value })}
                  >
                    <option value="">Choose a leg…</option>
                    {matchLegs.map((leg) => (
                      <option key={leg.id} value={leg.id}>
                        Leg {leg.leg_number}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="hd-meta">
                    {placeable ? 'No legs on that match' : 'Match finished — discard only'}
                  </span>
                )}

                <button
                  type="button"
                  className="hd-btn hd-btn-primary hd-btn-sm"
                  disabled={busy === item.id || !placeable || !chosen}
                  onClick={() => resolve(item, 'assign')}
                >
                  Assign
                </button>

                <button
                  type="button"
                  className="hd-btn hd-btn-danger hd-btn-sm"
                  disabled={busy === item.id}
                  onClick={() => resolve(item, 'discard')}
                >
                  Discard
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
